/**
 * Plain-text helpers over the markdown the admin rich-text toolbar produces.
 *
 * Deliberately dependency-free: the public organization pages and their unit
 * tests import this directly, and anything that pulled in the Supabase client
 * would throw at import time without the runtime environment variables. The
 * signed-in newsroom (src/components/organization/news/newsContent.ts) re-exports
 * these so both surfaces derive the same excerpt and read-time from one body.
 */

/** Strip the markdown the rich-text toolbar produces down to plain prose. */
export function stripMarkdown(md: string): string {
  if (!md) return "";
  return md
    .replace(/```[\s\S]*?```/g, " ") // fenced code
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links → text
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^\s*>\s?/gm, "") // blockquotes
    .replace(/^\s*[-*+]\s+/gm, "") // bullets
    .replace(/^\s*\d+\.\s+/gm, "") // numbered lists
    .replace(/^---$/gm, " ") // horizontal rules
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/\*([^*]+)\*/g, "$1") // italic *
    .replace(/_([^_]+)_/g, "$1") // italic _
    .replace(/\s+/g, " ")
    .trim();
}

/** Read-time computed from the body — null when there is no body to read. */
export function readingTime(body: string): string | null {
  const text = stripMarkdown(body);
  if (!text) return null;
  const words = text.split(/\s+/).filter(Boolean).length;
  if (!words) return null;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

/** A short plain-text excerpt computed from the body, broken on a word boundary. */
export function excerpt(body: string, max = 180): string {
  const text = stripMarkdown(body);
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
